import ReactGA from 'react-ga4'
import React, {
  useMemo,
  useState,
  useEffect,
  Suspense,
  useCallback,
  useRef,
} from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  ScrollRestoration,
  useNavigate,
  useParams,
  Navigate,
  useLocation,
} from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import SEO from './components/SEO'
import { getCurrentDomain } from './utils/seoConfig'
import {StaticPageWrapper, LawPageWrapper, TaxTreatyPageWrapper, CountryNewsPageWrapper} from './components/RouteWrappers'
import { generateWebsiteSchema } from './services/schemaGenerators'
import {
  Box,
  CssBaseline,
  CircularProgress,
  Container,
  Typography,
  Button,
  Snackbar,
  Alert,
} from '@mui/material'
import Pricing from './components/Pricing'
import Checkout from './components/Checkout'
import ProtectedRoute, { RoleProtectedRoute } from './components/ProtectedRoute'
import clarityService from './services/ClarityService'
import QuotaStatusBanner from './components/monetization/QuotaStatusBanner'
import CheckoutSuccess from './components/CheckoutSuccess'
// import List from './components/laws/List'
// import DecisionList from './components/decisions/DecisionList'
// import GuideList from './components/guides/GuideList'
// import PDFViewer from './components/pdf-viewer/PDFViewer'
// import GlobalSearchInLawSSP from './components/globalSearch/GlobalSearchInLawSSP'
// import RenderHTML from './components/RenderHTML'
// import Disclaimer from './components/Disclaimer'
// import Waitlist from './components/landingPage/Waitlist'
// import UniversalSearchListDocs from './components/UniversalSearchListDocs'
// import UniversalSearchGCC from './components/UniversalSearchGCC'
// import UniversalSearchMetrics from './components/UniversalSearchMetrics'
// import LawDetailsPage from './components/LawDetailsPage'
// import ArticleDetailPage from './components/ArticleDetailPage'
// import ArticleDetailPageV2 from './components/ArticleDetailPageV2'
// import ArticleDetailPageV3 from './components/ArticleDetailPageV3'
// import ArticleDetailPageV4 from './components/ArticleDetailPageV4'
import EnterpriseAccessPage from './components/EnterpriseAccessPage'
import BlogDetail from './components/BlogDetail'
import BlogWriter from './components/BlogWriter'
import PublicProfile from './components/PublicProfile'
import usePageTracking from './services/usePageTracking'
// import AdminPanel from './components/AdminPanel'
import AdminPanel from './components/admin-panel/pages/AdminPanel'
import Profile from './components/Profile'
// Keep critical components loaded immediately (for better SEO)
import ArticleDetail from './components/laws/ArticleDetail'
import DecisionDetail from './components/decisions/DecisionDetail'
import GuideDetail from './components/guides/GuideDetail'
import TaxTreatyDetail from './components/taxTreaty/TaxTreatyDetail'
// import Error from './components/Error'
import Error404 from './components/Error404'
import NavBar from './components/homepage2/components/NavBar'
import Footer from './components/homepage2/components/Footer'
import NewsTicker from './components/news/NewsTicker'
import WarningPopup from './components/WarningPopup'
import CookieConsentBanner from './components/ClarityCookieConsentBanner'
import SignIn from './components/SignIn'
// import ThankYou from './components/ThankYou'
// import AuthCallbackHandler from './context/AuthCallbackHandler'
// import { SupabaseProvider, useSupabase } from './context/SupabaseContext'
import { useAuth } from './context/AuthContext'
import { activityTracker } from './services/ActivityTracker'
import { useRouteTracking } from './hooks/useRouteTracking'
// Import the CountryProvider so that we dont need to hard countries in navbar links
import { CountryLawProvider } from './context/CountryLawContext'
import { AnnotationProvider } from './context/AnnotationContext'

// Keep law structure pages immediate - they're important for SEO
import HierarchicalArticlesView from './components/laws/HierarchicalArticlesView'
import HierarchicalLawStructure from './components/laws/HierarchicalLawStructure'
import Blogs from './components/Blogs'
// Lazy load non-critical components (won't affect SEO)
const Homepage = React.lazy(() =>
  import('./components/homepage2/pages/Homepage')
)
const CookiePolicy = React.lazy(() => import('./components/CookiePolicy'))
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'))
const TermsAndConditions = React.lazy(() =>
  import('./components/TermsAndConditions')
)
const PartnerWithUs = React.lazy(() => import('./components/PartnerWithUs'))
const AboutUs = React.lazy(() => import('./components/AboutUs'))
const ContactUs = React.lazy(() => import('./components/ContactUs'))
const Features = React.lazy(() => import('./components/Features'))
const UnsubscribePage = React.lazy(() => import('./components/UnsubscribePage'))
const GlobalSearchInLawCSP = React.lazy(() =>
  import('./components/globalSearch/GlobalSearchInLawCSP')
)
const GlobalSearchInTaxTransparencyCSP = React.lazy(() =>
  import('./components/globalSearch/GlobalSearchInTaxTransparencyCSP')
)
const TaxTreatyList = React.lazy(() =>
  import('./components/taxTreaty/TaxTreatyList')
)
const NewsLandingPage = React.lazy(() =>
  import('./components/news/NewsLandingPage')
)
const CountryNewsPage = React.lazy(() =>
  import('./components/news/CountryNewsPage')
)
const OAuth2Callback = React.lazy(() => import('./context/OAuth2Callback'))

const Root = () => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const isHomepage = location.pathname === '/'
  useRouteTracking(isAuthenticated)
  usePageTracking() // GA4 page tracking

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <CssBaseline />
      {/* <WarningPopup /> */}
      {/* <Container>
      <Typography variant="h2">GCC Laws Search Portal</Typography>
    </Container> */}
      <NavBar />
      {isHomepage && <NewsTicker />}
      {isAuthenticated && <QuotaStatusBanner />}
      {/* We can't see footer unless we scroll once - to make these changes we've added such code */}

      {/* Main content area with minimum height to ensure scrolling */}
      <Box
        sx={{
          flex: 1,
          minHeight: '100vh', // 'calc(100vh + 1px)' Ensures content is just taller than viewport
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Outlet wrapper to handle content */}
        <Box
          sx={{
            flex: 1,
            minHeight: '100%', // Take up full height of parent
          }}
        >
          <Outlet />
        </Box>
        {/* Optional: Add white space if content is insufficient */}
        {/* <Box
          sx={{
            flexGrow: 1,
            minHeight: 0, // Allow it to grow but not shrink below 0
          }}
        /> */}

        <ScrollRestoration />
      </Box>
      <Footer />
    </Box>
  )
}
// Protected Route Component
// const ProtectedRoute2 = ({ children }) => {
//   const navigate = useNavigate()
//   const { user, loading } = useSupabase()
//   if (loading) {
//     return (
//       <Box
//         sx={{
//           display: 'flex',
//           flexDirection: 'column',
//           alignItems: 'center',
//           justifyContent: 'center',
//           height: '100vh',
//           backgroundColor: '#232536',
//           textAlign: 'center',
//         }}
//       >
//         <CircularProgress
//           sx={{
//             color: '#ffda1b',
//             marginBottom: '20px',
//           }}
//         />
//         <Typography
//           variant='h6'
//           sx={{
//             color: 'white',
//           }}
//         >
//           Checking authentication...
//         </Typography>
//       </Box>
//     )
//   }
//   if (!user) {
//     return <Navigate to='/register' replace />
//   }
//   return children
// }
const initializeGA = () => {
  const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID

  if (gaId) {
    ReactGA.initialize(gaId, {
      debug: process.env.REACT_APP_ENVIRONMENT === 'dev',
      // Optional: Add other configuration options
      // gtagOptions: {
      //   send_page_view: true, // fasle, if you want to manually control page views
      // },
    })
  } else {
    console.warn('Google Analytics measurement ID not found')
  }
}
// Loading component for Suspense
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '300px',
      gap: 2,
    }}
  >
    <CircularProgress sx={{ color: '#232536' }} />
    <Typography variant='body2' color='text.secondary'>
      Loading content...
    </Typography>
  </Box>
)

const App = () => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const helmetContext = useMemo(() => ({}), [])
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [userConsent, setUserConsent] = useState(null)
  const isProd = process.env.REACT_APP_ENVIRONMENT === 'prod'
  const [printBlockedOpen, setPrintBlockedOpen] = useState(false)
  const [blockedActionMessage, setBlockedActionMessage] = useState(
    'Print operation is not allowed in this app.',
  )
  const trackerInitialized = useRef(false)
  const lastPrintNoticeAt = useRef(0)

  const showPrintBlockedMessage = useCallback((message) => {
    const now = Date.now()
    if (now - lastPrintNoticeAt.current < 1000) return
    lastPrintNoticeAt.current = now
    setBlockedActionMessage(message || 'Print operation is not allowed in this app.')
    setPrintBlockedOpen(true)
  }, [])

  const handlePrintBlockedClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return
    setPrintBlockedOpen(false)
  }, [])

  const handleConsentChange = useCallback((consent) => {
    setUserConsent(consent)
    // Update Clarity with consent status
    clarityService.updateConsent(consent)

    // Track consent event if analytics consent given
    if (consent.analytics) {
      clarityService.trackEvent('cookie_consent_granted', {
        essential: consent.essential,
        analytics: consent.analytics,
        timestamp: consent.timestamp,
      })
    }
  }, [])

  useEffect(() => {
    initializeGA()
  }, [])

  useEffect(() => {
    if (!isProd) return

    const originalPrint = window.print

    const blockPrint = () => {
      showPrintBlockedMessage('Print operation is not allowed in this app.')
    }

    const handlePrintShortcut = (event) => {
      const key = event.key?.toLowerCase()
      const hasModifier = event.ctrlKey || event.metaKey
      if (!hasModifier) return

      const isBlockedShortcut = key === 'p' || key === 's' || key === 'u'
      if (!isBlockedShortcut) return

      event.preventDefault()
      event.stopPropagation()

      if (key === 'p') {
        showPrintBlockedMessage('Print operation is not allowed in this app.')
        return
      }

      showPrintBlockedMessage('This keyboard shortcut is disabled.')
    }

    const handleContextMenu = (event) => {
      event.preventDefault()
      showPrintBlockedMessage('Right click is disabled.')
    }

    const handleBeforePrint = (event) => {
      event.preventDefault?.()
      showPrintBlockedMessage('Print operation is not allowed in this app.')
    }

    const handleClickCapture = (event) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const trigger = target.closest('button, a, [role="button"], [aria-label], [title]')
      if (!trigger) return

      const ariaLabel = trigger.getAttribute('aria-label') || ''
      const title = trigger.getAttribute('title') || ''
      const text = `${ariaLabel} ${title}`.toLowerCase()

      if (!text.includes('print')) return

      event.preventDefault()
      event.stopPropagation()
      showPrintBlockedMessage('Print operation is not allowed in this app.')
    }

    window.print = blockPrint
    window.addEventListener('beforeprint', handleBeforePrint)
    document.addEventListener('keydown', handlePrintShortcut, true)
    document.addEventListener('contextmenu', handleContextMenu, true)
    document.addEventListener('click', handleClickCapture, true)

    return () => {
      window.print = originalPrint
      window.removeEventListener('beforeprint', handleBeforePrint)
      document.removeEventListener('keydown', handlePrintShortcut, true)
      document.removeEventListener('contextmenu', handleContextMenu, true)
      document.removeEventListener('click', handleClickCapture, true)
    }
  }, [isProd, showPrintBlockedMessage])

  useEffect(() => {
    // Track initial page load in Clarity
    clarityService.trackPageView(window.location.pathname, {
      isInitialLoad: true,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    })

    // Check if returning from auth flow
    const authFlowStarted = sessionStorage.getItem('auth_flow_started')
    if (authFlowStarted) {
      sessionStorage.removeItem('auth_flow_started')
      clarityService.trackEvent('auth_flow_return', {
        timestamp: new Date().toISOString(),
        path: window.location.pathname,
        authDuration:
          Date.now() -
          parseInt(sessionStorage.getItem('auth_start_time') || '0'),
      })
    }

    // Set up user identification if already authenticated
    if (isAuthenticated && user) {
      clarityService.setUserId(user.id || user.email)
    }

    // Mark initial load as complete
    setInitialLoadComplete(true)
  }, [isAuthenticated, user])

  // Initialize activity tracking - Initialize/destroy activity tracking based on auth
  useEffect(() => {
    // Skip during initial loading
    if (isLoading) return

    if (isAuthenticated && user) {
      // Initialize ONLY ONCE
      if (!trackerInitialized.current) {
        activityTracker.initialize()
        trackerInitialized.current = true

        // Set user identity in Clarity
        clarityService.setUserId(user.id || user.email)

        // Track user session start
        clarityService.trackEvent('user_session_start', {
          userEmail: user.email,
          userRoles: user.roles,
          timestamp: new Date().toISOString(),
        })

        // Track initial app initialization
        activityTracker.trackCustomEvent('app_initialized', {
          userEmail: user.email,
          userRoles: user.roles,
          timestamp: new Date().toISOString(),
        })
      }
    } else {
      if (trackerInitialized.current) {
        // AWAIT destroy() to ensure session end completes
        activityTracker.destroy().then(() => {
          trackerInitialized.current = false

          // Track logout in Clarity (only if not during initial load)
          if (initialLoadComplete) {
            clarityService.trackEvent('user_session_end', {
              timestamp: new Date().toISOString(),
            })
          }
        })
      }
    }
    // Cleanup on unmount
    return () => {
      if (trackerInitialized.current) {
        activityTracker.destroy().then(() => {
          trackerInitialized.current = false
        })
      }
    }
  }, [isAuthenticated, user, isLoading, initialLoadComplete])

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: <Root />,
          errorElement: <Error404 />,
          children: [
            // {
            //   path: '/api',
            //   element: (
            //     <Navigate
            //       to='/api/fintegerllp/decreehub/api/swagger-ui/index.html'
            //       replace
            //     />
            //   ),
            // },
            {
              path: '/',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    useDomainSEO
                    // title='GCC Tax Laws | Premier GCC Tax Research Platform'
                    // description='Searchable GCC tax database: UAE VAT & Corporate Tax (CIT) Saudi/KSA VAT customs duty excise transfer pricing and tax treaties (DTAA). Kuwait Oman Qatar Bahrain.'
                    // canonical={`https://${getCurrentDomain()}/`}
                    // keywords='GCC tax laws, UAE tax law, Saudi Arabia tax, Qatar tax, Kuwait tax, Bahrain tax, Oman tax, tax research, corporate tax, VAT law'
                    schemaMarkup={generateWebsiteSchema()}
                  >
                    <Homepage />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/login',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Sign In | GCC Tax Laws'
                    description='Sign in to GCC Tax Laws to access premium tax research features, search tools, and comprehensive legal databases for the GCC region.'
                    canonical={`https://${getCurrentDomain()}/login`}
                    keywords='login GCC tax laws, tax research sign in, user account access, premium tax features'
                  >
                    <SignIn />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/profile',
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <StaticPageWrapper
                      title='User Profile | GCC Tax Laws'
                      description='Manage your GCC Tax Laws account settings, subscription details, and personalize your tax research experience. Update preferences and view usage analytics.'
                      canonical={`https://${getCurrentDomain()}/profile`}
                      keywords='user profile, account settings, subscription management, tax research preferences, GCC tax laws account'
                    >
                      <Profile />
                    </StaticPageWrapper>
                  </Suspense>
                </ProtectedRoute>
              ),
            },

            {
              path: '/oauth2/callback/success',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <SEO
                    title='Authenticating... | GCC Tax Laws'
                    description='Processing authentication...'
                  />
                  <OAuth2Callback />
                </Suspense>
              ),
            },
            {
              path: '/admin',
              element: (
                <RoleProtectedRoute allowedRoles={['ADMIN']}>
                  <Suspense fallback={<PageLoader />}>
                    <StaticPageWrapper
                      title='Admin Panel | GCC Tax Laws'
                      description='Administrator dashboard for managing GCC Tax Laws platform. Access content management, user administration, and system analytics tools.'
                      canonical={`https://${getCurrentDomain()}/admin`}
                      keywords='admin panel, content management, user administration, system dashboard, platform management'
                    >
                      <AdminPanel />
                    </StaticPageWrapper>
                  </Suspense>
                </RoleProtectedRoute>
              ),
            },
            {
              path: '/unauthorized',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Unauthorized | GCC Tax Laws'
                    description='You do not have permission to access this page.'
                    canonical={`https://${getCurrentDomain()}/unauthorized`}
                  >
                    <UnauthorizedPage />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            // {
            //   path: '/auth/callback',
            //   element: <AuthCallbackHandler />,
            // },
            // {
            //   path: '/success',
            //   element: (
            //     <ProtectedRoute2>
            //       <ThankYou />
            //     </ProtectedRoute2>
            //   ),
            // },
            {
              path: '/search-across-law',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    useDomainSEO
                    // title='Search GCC Tax Laws | Global Tax Law Search'
                    // description='Search across all GCC tax laws, articles, decisions, and guidance. Find relevant tax information quickly and efficiently across UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, and Oman.'
                    // canonical={`https://${getCurrentDomain()}/search-across-law`}
                    // keywords='search tax laws, GCC tax search, UAE tax search, find tax articles, tax law search engine'
                  >
                    <GlobalSearchInLawCSP />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/search-across-tax-transparency',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper useDomainSEO>
                    <GlobalSearchInTaxTransparencyCSP />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            // {
            //   path: '/search-across-country',
            //   element: (
            //     <ProtectedRoute>
            //       <Suspense fallback={<PageLoader />}>
            //         <StaticPageWrapper
            //           title='Search by Country | GCC Tax Laws Research'
            //           description='Search tax laws by specific GCC country. Find UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, and Oman tax regulations, decisions, and guidance with advanced country-specific filters.'
            //           canonical={`https://${getCurrentDomain()}/search-across-country`}
            //           keywords='country specific tax search, UAE tax laws, Saudi Arabia tax, Qatar tax regulations, Kuwait tax law, Bahrain tax, Oman tax, GCC country search'
            //         >
            //           <UniversalSearchMetrics />
            //         </StaticPageWrapper>
            //       </Suspense>
            //     </ProtectedRoute>
            //   ),
            // },
            {
              path: '/laws/:country/:lawShortName/:path',
              element: (
                <ProtectedRoute>
                  <LawPageWrapper useDomainSEO>
                    <HierarchicalArticlesView />
                  </LawPageWrapper>
                </ProtectedRoute>
              ),
            },
            {
              path: '/laws/:country/:lawShortName',
              element: (
                <ProtectedRoute>
                  <LawPageWrapper useDomainSEO>
                    <HierarchicalLawStructure />
                  </LawPageWrapper>
                </ProtectedRoute>
              ),
            },
            {
              path: '/search/:country/Tax-Treaties',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <TaxTreatyPageWrapper useDomainSEO>
                    <TaxTreatyList />
                  </TaxTreatyPageWrapper>
                </Suspense>
              ),
            },
            // Critical content pages - NOT lazy loaded for better SEO
            {
              path: '/articles/:slug',
              element: (
                <ProtectedRoute>
                  <ArticleDetail />
                </ProtectedRoute>
              ),
            },

            {
              path: '/decisions/:slug',
              element: (
                <ProtectedRoute>
                  <DecisionDetail />
                </ProtectedRoute>
              ),
            },
            {
              path: '/guidances/:slug',
              element: (
                <ProtectedRoute>
                  <GuideDetail />
                </ProtectedRoute>
              ),
            },
            {
              path: '/tax-treaties/:slug',
              element: (
                <ProtectedRoute>
                  <TaxTreatyDetail />
                </ProtectedRoute>
              ),
            },

            // Static pages with SEO
            {
              path: '/about-us',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    useDomainSEO
                    // title='About Us | GCC Tax Laws'
                    // description='Learn about GCC Tax Laws platform, our mission to provide comprehensive tax research tools for the GCC region. Trusted by tax professionals across the Middle East.'
                    // canonical={`https://${getCurrentDomain()}/about-us`}
                    // keywords='about GCC tax laws, tax research platform, Middle East tax, GCC tax experts'
                  >
                    <AboutUs />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/partner-with-us',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper useDomainSEO
                  // title='Partner with Us | GCC Tax Laws'
                  // description='Join our partnership program at GCC Tax Laws. Collaborate with universities, firms, consultants, and corporate teams to advance tax knowledge across the GCC region.'
                  // canonical={`https://${getCurrentDomain()}/partner-with-us`}
                  // keywords='GCC tax partnerships, tax law collaboration, university partnerships, consulting partnerships, corporate tax training, GCC tax experts, revenue sharing, content partnerships'
                  >
                    <PartnerWithUs />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/contact-us',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper useDomainSEO
                  // title='Contact Us | GCC Tax Laws'
                  // description='Get in touch with GCC Tax Laws team. Contact us for support, partnerships, or inquiries about our tax research platform. We are here to help.'
                  // canonical={`https://${getCurrentDomain()}/contact-us`}
                  // keywords='contact GCC tax laws, tax research support, customer service, partnerships'
                  >
                    <ContactUs />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/features',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper useDomainSEO
                  // title='Features | GCC Tax Laws Platform'
                  // description='Explore powerful features of GCC Tax Laws platform including advanced search, document analysis, tax compliance tools, and comprehensive legal databases.'
                  // canonical={`https://${getCurrentDomain()}/features`}
                  // keywords='tax research features, advanced search, document analysis, compliance tools, legal database'
                  >
                    <Features />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/cookie-policy',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Cookie Policy | GCC Tax Laws'
                    description='Learn about how we use cookies on GCC Tax Laws platform to improve your browsing experience and provide personalized content.'
                    canonical={`https://${getCurrentDomain()}/cookie-policy`}
                  >
                    <CookiePolicy />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/privacy-policy',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Privacy Policy | GCC Tax Laws'
                    description='Read our privacy policy to understand how we collect, use, and protect your information on GCC Tax Laws platform. Your privacy is important to us.'
                    canonical={`https://${getCurrentDomain()}/privacy-policy`}
                  >
                    <PrivacyPolicy />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/unsubscribe',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Email Preferences | GCC Tax Laws'
                    description='Manage your GCC Tax Laws promotional email subscription preferences.'
                    canonical={`https://${getCurrentDomain()}/unsubscribe`}
                  >
                    <UnsubscribePage />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/terms-and-conditions',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Terms and Conditions | GCC Tax Laws'
                    description='Read our terms and conditions for using GCC Tax Laws platform and accessing our tax research services. Legal terms and user agreement.'
                    canonical={`https://${getCurrentDomain()}/terms-and-conditions`}
                  >
                    <TermsAndConditions />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/enterprise-access',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper
                    title='Enterprise Access | GCC Tax Laws'
                    description='Enterprise access information and firewall configuration guidelines'
                    canonical={`https://${getCurrentDomain()}/enterprise-access`}
                    keywords='enterprise access, firewall configuration, IT whitelist, corporate access'
                  >
                    <EnterpriseAccessPage />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/news',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <StaticPageWrapper useDomainSEO>
                    <NewsLandingPage />
                  </StaticPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/news/:country',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CountryNewsPageWrapper>
                    <CountryNewsPage />
                  </CountryNewsPageWrapper>
                </Suspense>
              ),
            },
            {
              path: '/blogs',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <Blogs />
                </Suspense>
              ),
            },
            {
              path: '/blogs/:slug',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <BlogDetail />
                </Suspense>
              ),
            },
            {
              path: '/blogs/write',
              element: (
                <ProtectedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <BlogWriter />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
            {
              path: '/:username',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PublicProfile />
                </Suspense>
              ),
            },
            {
              path: '/pricing',
              element: <Pricing />,
            },
            {
              path: '/checkout',
              element: (
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              ),
            },
            {
              path: '/checkout/success',
              element: (
                <ProtectedRoute>
                  <CheckoutSuccess />
                </ProtectedRoute>
              ),
            },
            // {
            //   path: '/deepsearch-across-country',
            //   element: <UniversalSearchListDocs />,
            // },
            // {
            //   path: '/search-across-gcc',
            //   element: <UniversalSearchGCC />,
            // },
            // {
            //   path: '/law/:lawSlug',
            //   element: <LawDetailsPage />,
            // },
            // {
            //   path: '/law/:lawId/article/:articleId',
            //   element: <ArticleDetailPage />,
            // },
            // {
            //   path: '/law/:lawId/articlev2/:articleId',
            //   element: <ArticleDetailPageV2 />,
            // },
            // {
            //   path: '/law/:lawId/articlev3/:articleId',
            //   element: <ArticleDetailPageV3 />,
            // },
            // {
            //   path: '/law/:lawId/articlev4/:articleId',
            //   element: <ArticleDetailPageV4 />,
            // },
            // {
            //   path: '/law/:lawId/guideline/:guidelineId',
            //   element: <div>Guideline Detail Page</div>,
            // },
            // {
            //   path: '/law/:lawId/decision/:decisionId',
            //   element: <div>Decision Detail Page</div>,
            // },

            {
              path: '*', // Add this catch-all route
              element: <Error404 />,
            },
            //            {
            //              path: '/pdf',
            //              element: <PDFViewer />,
            //            },
          ],
        },
      ]),
    [],
  )
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }
  return (
    <HelmetProvider context={helmetContext}>
      <CountryLawProvider>
        <AnnotationProvider>
          <CookieConsentBanner onConsentChange={handleConsentChange} />
          <RouterProvider router={router} />
          {isProd && (
            <Snackbar
              open={printBlockedOpen}
              autoHideDuration={2500}
              onClose={handlePrintBlockedClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                severity='warning'
                onClose={handlePrintBlockedClose}
                sx={{ width: '100%' }}
              >
                {blockedActionMessage}
              </Alert>
            </Snackbar>
          )}
        </AnnotationProvider>
      </CountryLawProvider>
    </HelmetProvider>
  )
}
const UnauthorizedPage = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60vh',
      textAlign: 'center',
      gap: 2,
    }}
  >
    <Typography variant='h4' color='error'>
      Access Denied
    </Typography>
    <Typography variant='body1' color='text.secondary'>
      You don't have permission to access this page.
    </Typography>
    <Button
      variant='contained'
      onClick={() => window.history.back()}
      sx={{ mt: 2 }}
    >
      Go Back
    </Button>
  </Box>
)
export default App
